import { useTheme } from "@smartrent/ui";

import {
  CLIMATE_PROTECTION_BEHAVIOR_LABELS,
  climateProtectionBehaviorLabels,
  formatClimateProtectionRuntime,
  formatClimateProtectionThreshold,
} from "@/utils/climateProtection";
import { ClimateProtectionBehavior } from "@/types";
import helpers from "@/lib/helpers";

import { useUnit } from "./UnitContext";

// Climate Protection Mode is only supported on Hub+ and Fusion V3 hubs, so
// these details are only meaningful when the unit's hub is one of those.
// Legacy Fusion (serial-derived "fusion") is intentionally excluded.
const CLIMATE_PROTECTION_HUB_TYPES = ["Hub+", "Fusion V3"];

function ClimateProtectionRow({
  name,
  value,
}: {
  name: string;
  value?: string | null;
}) {
  const { colors } = useTheme();

  return (
    <div
      style={{
        backgroundColor: colors.pageBackground,
        padding: "4px",
        borderTop: "1px solid #eee",
        borderBottom: "1px solid #eee",
      }}
    >
      <div className="u-flex u-flex-align-items-center u-flex-justify-between">
        <div>
          <span className="h6">{name}</span>
          {value ? ` (${value})` : null}
        </div>
      </div>
    </div>
  );
}

/**
 * Renders configured (and, when applicable, active) Climate Protection details
 * as device-card fields. Intended to be shown only on thermostat cards. The
 * explanatory support guidance lives in the active headline, not here.
 */
export default function DeviceClimateProtection() {
  const { climateProtection, hub } = useUnit();

  // Only Hub+ and Fusion hubs support Climate Protection Mode.
  if (!hub || !CLIMATE_PROTECTION_HUB_TYPES.includes(helpers.getHubType(hub))) {
    return null;
  }

  const configured = climateProtection?.configured;

  // Configured state is independent of operating state — only render the
  // fields when the property has enabled the settings.
  if (!configured?.active) {
    return null;
  }

  const {
    behaviors = [],
    thresholds = {},
    maximum_runtime_minutes,
  } = configured;
  const runtime = formatClimateProtectionRuntime(maximum_runtime_minutes);

  const operating = climateProtection?.operating;
  const activeLabels = operating?.active
    ? climateProtectionBehaviorLabels(operating.behaviors)
    : [];

  return (
    <div>
      <ClimateProtectionRow name="Climate Protection" value="Enabled" />

      {behaviors.map((behavior: ClimateProtectionBehavior) => (
        <ClimateProtectionRow
          key={behavior}
          name={CLIMATE_PROTECTION_BEHAVIOR_LABELS[behavior] ?? behavior}
          value={formatClimateProtectionThreshold(
            behavior,
            thresholds?.[behavior]
          )}
        />
      ))}

      {runtime ? (
        <ClimateProtectionRow name="Maximum Runtime" value={runtime} />
      ) : null}

      {activeLabels.length ? (
        <ClimateProtectionRow
          name="Climate Protection Active"
          value={activeLabels.join(", ")}
        />
      ) : null}
    </div>
  );
}
